import { useMemo } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import styled from "styled-components";
import { CardComp, ButtonComp } from "../../../components";
import InvestorPageShell from "../../../components/Investor/InvestorPageShell";
import InvestorProfileNav, { getProfileTab } from "../../../components/Investor/InvestorProfileNav";
import InvestorKycPanel from "../../../components/Investor/InvestorKycPanel";
import InvestorRequestsList from "../../../components/Investor/InvestorRequestsList";
import InvestorStatusBadge from "../../../components/Investor/InvestorStatusBadge";
import { investorButtonProps } from "../../../components/Investor/investorButtonStyles";
import { useInvestorSession } from "../../../hooks/useInvestorSession";
import { useListInvestorInvestmentRequestsQuery } from "../../../store/investor/investorInvestmentsApi";
import { mapInvestorRequestToListShape } from "../../../utils/investorInvestmentsAdapter";
import { logout } from "../../../store/auth/index";
import { convertToTitleCase } from "../../../utils/helpers";
import TableSkeletonLoader from "../../../components/Common/TableSkeletonLoader";

const DetailRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.85rem 0;
  border-bottom: 1px solid #f1f1f1;

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }

  .label {
    font-size: 0.75rem;
    color: #525252;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    font-weight: 600;
  }

  .value {
    font-size: 0.95rem;
    color: #383838;
    font-weight: 500;
    word-break: break-word;
  }
`;

const ProfileCard = styled(CardComp).attrs({ noDefaultPadding: true })`
  && {
    margin-bottom: 0;
    padding: 0;

    .card-body {
      padding: 1.25rem 1.35rem;
    }

    @media (min-width: 768px) {
      .card-body {
        padding: 1.5rem 1.65rem;
      }
    }
  }
`;

export default function InvestorProfilePage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const activeTab = getProfileTab(searchParams);
  const { profile, kycStatus, kycRecord, isMember, isLoggedIn } = useInvestorSession();

  const { data: apiData, isFetching } = useListInvestorInvestmentRequestsQuery({
    page: 1,
    per_page: 20,
  });

  const requests = useMemo(
    () => (apiData?.items || []).map(mapInvestorRequestToListShape).filter(Boolean),
    [apiData?.items]
  );

  const handleLogout = () => {
    if (isLoggedIn) {
      dispatch(logout());
      navigate("/login");
    }
  };

  const accountType = isMember ? "Cooperative member + investor" : "Standalone investor";

  return (
    <InvestorPageShell
      title="Profile"
      subtitle="Manage your account, identity verification, and investor activity."
      status={kycStatus !== "approved" ? kycStatus : null}
      statusLabel={kycStatus !== "approved" ? `KYC: ${convertToTitleCase(kycStatus)}` : null}
    >
      <InvestorProfileNav />

      {activeTab === "account" ? (
        <div className="row g-4">
          <div className="col-md-6">
            <ProfileCard cardClassName="border-0 shadow-sm rounded-4 h-100">
              <h6 className="fw-bold mb-3" style={{ color: "#383838" }}>
                Account details
              </h6>
              <DetailRow>
                <span className="label">Name</span>
                <span className="value">{profile?.full_name || "—"}</span>
              </DetailRow>
              <DetailRow>
                <span className="label">Email</span>
                <span className="value">{profile?.email || "—"}</span>
              </DetailRow>
              <DetailRow>
                <span className="label">Phone</span>
                <span className="value">{profile?.phone || "—"}</span>
              </DetailRow>
              <DetailRow>
                <span className="label">Account type</span>
                <span className="value">{accountType}</span>
              </DetailRow>
            </ProfileCard>
          </div>
          <div className="col-md-6">
            <ProfileCard cardClassName="border-0 shadow-sm rounded-4 h-100">
              <h6 className="fw-bold mb-3" style={{ color: "#383838" }}>
                Verification snapshot
              </h6>
              <div className="d-flex align-items-center gap-2 mb-3">
                <InvestorStatusBadge status={kycStatus} />
              </div>
              {kycRecord?.legal_name ? (
                <DetailRow>
                  <span className="label">Legal name</span>
                  <span className="value">{kycRecord.legal_name}</span>
                </DetailRow>
              ) : null}
              {kycRecord?.nin_masked ? (
                <DetailRow>
                  <span className="label">NIN on file</span>
                  <span className="value">{kycRecord.nin_masked}</span>
                </DetailRow>
              ) : null}
              <p className="text-muted small mb-0 mt-3">
                Open the <strong>Identity verification</strong> tab to submit or update your KYC details.
              </p>
            </ProfileCard>
          </div>
        </div>
      ) : null}

      {activeTab === "verification" ? (
        <div>
          <p className="text-muted small mb-3" style={{ maxWidth: "42rem", lineHeight: 1.6 }}>
            Complete NIN verification to access investments and other investor services.
          </p>
          <InvestorKycPanel />
        </div>
      ) : null}

      {activeTab === "activity" ? (
        <div>
          <h5 className="fw-bold mb-3" style={{ color: "#383838" }}>
            Investment requests ({requests.length})
          </h5>
          {isFetching ? (
            <TableSkeletonLoader rows={3} columns={2} />
          ) : (
            <InvestorRequestsList
              requests={requests}
              emptyMessage="No investment requests yet."
            />
          )}
        </div>
      ) : null}

      <div className="mt-5 pt-2" style={{ maxWidth: 320 }}>
        <ButtonComp
          btnText="Log out"
          onClick={handleLogout}
          {...investorButtonProps.danger}
        />
      </div>
    </InvestorPageShell>
  );
}
